import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export const ALL_ROLES = ['CUSTOMER', 'CREATOR', 'TEAMBOARD', 'TEAM', 'DRIVER', 'COLLECTOR', 'OFFICER', 'PLANNER', 'CONTROLLER', 'MANAGER', 'ADMIN'] as const;
export type RoleName = typeof ALL_ROLES[number];

/** German role labels; keys match the backend role names plus the merged CUSTOMER_CREATOR bucket the
 * "Users" stats tile (issue #67) reports. Matches the wording used in DASHBOARD_CONFIG. */
export const ROLE_LABELS: Record<RoleName | 'CUSTOMER_CREATOR', string> = {
  CUSTOMER: 'Kunde',
  CREATOR: 'Erfasser',
  CUSTOMER_CREATOR: 'Kunde/Erfasser',
  TEAMBOARD: 'Teamboard',
  TEAM: 'Team',
  DRIVER: 'Fahrer',
  COLLECTOR: 'Sammler',
  OFFICER: 'Sachbearbeiter',
  PLANNER: 'Planer',
  CONTROLLER: 'Controller',
  MANAGER: 'Manager',
  ADMIN: 'Admin'
};

/** Issue #65: per-user override for the Messaging system's mail dispatch - does not apply to password-reset
 * mail. Admin-maintained only for now, via the edit-user dialog. PAUSE leaves due mail queued so it catches up
 * once switched back to ON; MARK_READ settles it immediately (never sent, even retroactively) instead - it
 * doesn't actually mark the message read despite the name, see MessageService.markProcessedWithoutMailing(). */
export const MAIL_DELIVERY_STATUSES = ['ON', 'PAUSE', 'MARK_READ'] as const;
export type MailDeliveryStatus = typeof MAIL_DELIVERY_STATUSES[number];

export const MAIL_DELIVERY_STATUS_LABELS: Record<MailDeliveryStatus, string> = {
  ON: 'Mailversand aktiv',
  PAUSE: 'Mailversand pausiert (wird bei Reaktivierung nachgeholt)',
  MARK_READ: 'Mailversand aus (kein Nachversand)'
};

export interface UserSummary {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string;
  enabled: boolean;
  roles: RoleName[];
  createdAt: string;
  lastModified: string;
  mailDeliveryStatus: MailDeliveryStatus;
  /** Second factor (TOTP) is set up and active. */
  mfaEnabled: boolean;
}

export interface UpdateUserRequest {
  firstName: string | null;
  lastName: string;
  enabled: boolean;
  roles: RoleName[];
  mailDeliveryStatus: MailDeliveryStatus;
}

export interface CreateUserRequest {
  email: string;
  password: string;
  firstName: string | null;
  lastName: string;
  roles: RoleName[];
}

/** Admin-Kachel Users (issue #67). */
export interface UserStatsRoleCount {
  /** Role name, or 'CUSTOMER_CREATOR' for the merged self-service bucket - see ROLE_LABELS. */
  role: string;
  count: number;
}

export interface UserStats {
  totalCount: number;
  enabledCount: number;
  withoutMailNotificationCount: number;
  byRole: UserStatsRoleCount[];
}

/** Admin-Kachel Useraktivität (issue #68). Counts are distinct users active in each period (Europe/Berlin). */
export interface UserActivityTimeOfDayCount {
  /** e.g. "08-12". */
  label: string;
  count: number;
}

export interface UserActivityStats {
  dayBeforeYesterdayCount: number;
  yesterdayCount: number;
  todayCount: number;
  todayByTimeOfDay: UserActivityTimeOfDayCount[];
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly apiUrl = `${environment.apiUrl}/users`;

  constructor(private readonly http: HttpClient) {
  }

  list(): Observable<UserSummary[]> {
    return this.http.get<UserSummary[]>(this.apiUrl);
  }

  update(id: string, request: UpdateUserRequest): Observable<UserSummary> {
    return this.http.patch<UserSummary>(`${this.apiUrl}/${id}`, request);
  }

  /** Admin only: wipes the user's second factor (lost phone), so the next login sets it up again. */
  resetMfa(id: string): Observable<UserSummary> {
    return this.http.post<UserSummary>(`${this.apiUrl}/${id}/mfa/reset`, {});
  }

  create(request: CreateUserRequest): Observable<UserSummary> {
    return this.http.post<UserSummary>(this.apiUrl, request);
  }

  getStats(): Observable<UserStats> {
    return this.http.get<UserStats>(`${this.apiUrl}/stats`);
  }

  getActivityStats(): Observable<UserActivityStats> {
    return this.http.get<UserActivityStats>(`${this.apiUrl}/stats/activity`);
  }
}
